import { ArrowUp, Check, Target } from "lucide-react";

export const LABEL = { 1: "1×", 2: "2×", 3: "3×", 5: "5×", 7: "Daily" };

const stepStyle = (i, phase) => {
  if (i < phase) return "bg-emerald-100 border-emerald-200 text-emerald-800";
  if (i === phase) return "bg-stone-900 border-stone-900 text-amber-300";
  return "bg-white border-stone-200 text-stone-400";
};

export function LadderSteps({ habit }) {
  const { ladder, phase_index } = habit;
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {ladder.map((g, i) => (
        <div key={g} className="flex items-center gap-1.5">
          <div data-testid={`ladder-step-${habit.id}-${i}`} className={`px-2.5 py-1 rounded-full text-[11px] font-bold border transition ${stepStyle(i, phase_index)}`}>
            {i < phase_index ? <Check className="w-3 h-3 inline -mt-0.5 mr-0.5" /> : null}
            {LABEL[g]}/wk
          </div>
          {i < ladder.length - 1 && <div className="w-3 h-px bg-stone-300" />}
        </div>
      ))}
    </div>
  );
}

function WeeksOnTarget({ habit }) {
  const { weeks_met, weeks_to_advance, at_final_goal } = habit;
  if (at_final_goal) return <div className="mt-2 text-[11px] font-semibold text-emerald-700">Final goal reached — this is your rhythm now.</div>;
  return (
    <div className="flex items-center gap-2 mt-2 text-[11px] text-stone-500">
      <span>Weeks on target</span>
      <div className="flex gap-1">
        {Array.from({ length: weeks_to_advance }).map((_, i) => (
          <span key={`week-${i + 1}`} className={`w-2.5 h-2.5 rounded-full ${i < weeks_met ? "bg-emerald-500" : "bg-stone-200"}`} />
        ))}
      </div>
      <span className="font-mono">{weeks_met}/{weeks_to_advance}</span>
      <span className="text-stone-400">· beat the goal to jump ahead</span>
    </div>
  );
}

export function WeekProgress({ habit }) {
  const { goal, week_count } = habit;
  const pct = Math.min(100, Math.round((week_count / goal) * 100));
  return (
    <div>
      <div className="flex justify-between text-xs font-semibold text-stone-600 mb-1">
        <span className="inline-flex items-center gap-1">
          <Target className="w-3.5 h-3.5 text-amber-700" /> This week · goal {habit.goal_label.toLowerCase()}
        </span>
        <span className="font-mono text-amber-800" data-testid={`week-count-${habit.id}`}>{week_count}/{goal}</span>
      </div>
      <div className="h-2 rounded-full bg-stone-100 overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-500 ${pct >= 100 ? "bg-emerald-500" : "bg-amber-500"}`}
          style={{ width: `${Math.max(pct, week_count > 0 ? 6 : 0)}%` }} />
      </div>
      <WeeksOnTarget habit={habit} />
    </div>
  );
}

export function LevelUpPrompt({ habit, onLevelUp }) {
  if (!habit.pending_level_up) return null;
  return (
    <div className="rounded-2xl p-4 bg-gradient-to-r from-emerald-50 to-amber-50 border border-emerald-200 flex items-center justify-between gap-3 flex-wrap"
      data-testid={`levelup-prompt-${habit.id}`}>
      <div>
        <div className="text-xs font-bold uppercase tracking-widest text-emerald-800">You're ready to level up</div>
        <div className="text-sm text-stone-700 mt-0.5">
          Three weeks on target. Move to <strong>{LABEL[habit.ladder[habit.phase_index + 1]]}/week</strong>?
        </div>
      </div>
      <div className="flex gap-2">
        <button data-testid={`levelup-accept-${habit.id}`} onClick={() => onLevelUp(habit, true)}
          className="inline-flex items-center gap-1 px-4 py-2 rounded-full text-sm font-semibold bg-emerald-700 text-white hover:bg-emerald-800">
          <ArrowUp className="w-4 h-4" /> Level up
        </button>
        <button data-testid={`levelup-defer-${habit.id}`} onClick={() => onLevelUp(habit, false)}
          className="px-4 py-2 rounded-full text-sm font-semibold bg-white border border-stone-300 text-stone-700 hover:bg-stone-50">
          Stay a bit longer
        </button>
      </div>
    </div>
  );
}
